import { Fragment, ReactElement } from 'react';
import { useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { ZoneResult } from 'types/domainTypes';

const TableCell = styled(MuiTableCell)(() => ({
  padding: '0px 4px 8px',
  border: 0,
}));

type ZoneResultComponentProps = {
  results: ZoneResult[];
};

const ZoneResultComponent = ({ results }: ZoneResultComponentProps): ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();

  const labelCell = (label: string, halfWidth = false, padding = false) => (
    <TableCell sx={{ width: halfWidth ? '50%' : undefined, paddingTop: padding ? '16px' : undefined }}>
      <Typography variant="body2" sx={{ color: theme.palette.grey[500] }}>
        {label}
      </Typography>
    </TableCell>
  );

  const valueCell = (value: number | undefined) => <TableCell>{value}</TableCell>;

  const tableLayout = (zone: ZoneResult) => {
    const fields = [
      { label: t('zone-result.num-end-fasteners'), value: zone.endFasteners },
      { label: t('zone-result.min-end-reaction'), value: zone.minEndReaction },
      { label: t('zone-result.num-intermediate-fasteners'), value: zone.intermediateFasteners },
      { label: t('zone-result.min-intermediate-reaction'), value: zone.minIntermediateReaction },
    ].filter((field) => field.value !== undefined);

    if (fields.length === 1) {
      return (
        <>
          <TableRow sx={{ verticalAlign: 'top' }}>
            {labelCell(fields[0].label, true)}
            <TableCell></TableCell>
          </TableRow>
          <TableRow>
            {valueCell(fields[0].value)}
            <TableCell></TableCell>
          </TableRow>
        </>
      );
    } else if (fields.length === 2) {
      return (
        <>
          <TableRow sx={{ verticalAlign: 'top' }}>
            {labelCell(fields[0].label)}
            {labelCell(fields[1].label)}
          </TableRow>
          <TableRow>
            {valueCell(fields[0].value)}
            {valueCell(fields[1].value)}
          </TableRow>
        </>
      );
    }

    return (
      <>
        <TableRow sx={{ verticalAlign: 'top' }}>
          {labelCell(fields[0].label)}
          {labelCell(fields[1].label)}
        </TableRow>
        <TableRow>
          {valueCell(fields[0].value)}
          {valueCell(fields[1].value)}
        </TableRow>
        <TableRow sx={{ verticalAlign: 'top' }}>
          {labelCell(fields[2].label, false, true)}
          {labelCell(fields[3].label, false, true)}
        </TableRow>
        <TableRow>
          {valueCell(fields[2].value)}
          {valueCell(fields[3].value)}
        </TableRow>
      </>
    );
  };

  return (
    <Stack spacing={2} sx={{ padding: '16px' }}>
      <Typography>{t('zone-result.title')}</Typography>
      <Typography sx={{ color: theme.palette.grey[500] }}>{t('zone-result.subtitle')}</Typography>

      {results.map((zone, index) => (
        <Fragment key={index}>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Stack spacing={1} width="33%">
              <Typography variant="body2">{zone.name}</Typography>
              <Typography variant="body2">
                {t('zone-result.spans', { count: zone.numberOfSpans })} ({zone.spanLength} m)
              </Typography>
            </Stack>
            <Stack width="66%">
              <Table key={index}>
                <TableBody>{tableLayout(zone)}</TableBody>
              </Table>
            </Stack>
          </Stack>
        </Fragment>
      ))}
    </Stack>
  );
};

export default ZoneResultComponent;
